import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi, TreeApi } from 'react-arborist';

import { useAddToast } from '../../../../contexts/ToastContext';
import { getSnippetPaths, getSnippetPathsQueryKey } from '../../../../hooks/api/snippets/useSnippetPaths';
import type { GetSnippetPathsParams } from '../../../../../shared/snippets';
import type { SnippetTreeNodeData } from '../types';

import SnippetsTreeViewNode from './Node';

import styles from './SnippetsTreeView.module.less';

//  Note that this is set in the CSS, but we want to ensure they are the same.
const TREE_WIDTH = 250;

// We actually don't initially fetch anything, but we need to display a root node which users can expand.
const ROOT_NODE: SnippetTreeNodeData = { type: 'directory', name: '[root]', path: '/', children: [] };

/**
 * Function to recursively "inject" new children into the tree.
 *
 * @param {SnippetTreeNodeData[]} nodes - The nodes to inject into.
 * @param {string} parentPath - The path to the parent node.
 * @param {SnippetTreeNodeData[]} children - The new children to inject.
 * @returns {SnippetTreeNodeData[]} - The new tree with the children injected.
 */
function injectChildren(
  nodes: SnippetTreeNodeData[],
  parentPath: string,
  children: SnippetTreeNodeData[],
): SnippetTreeNodeData[] {
  return nodes.map((node) => {
    //  If the node's path matches the parent path, we need to inject the new children into it.
    if (node.path === parentPath) {
      return { ...node, children, hasLoaded: true };
    }
    return node.children
      ? {
          ...node,
          //  Recursively inject the new children into the node's children.
          children: injectChildren(node.children, parentPath, children),
        }
      : node;
  });
}

type Props = {
  userRevisionLocator: string;
  selectedPath: string;
  setSelectedPath: (path: string) => void;
};

/**
 * SnippetsTreeView renders the file tree for a given userRevisionLocator,
 * allowing the user to expand and load directories in a given file tree.
 * It will load the tree lazily, only loading the children of a directory when it is expanded.
 *
 * These results are cached via React Query, but because we can only pass a static component to react-arborist,
 * we aren't using React Query in the typical way here (instead we are manually ccalling the fetch function directly).
 *
 * react-arborist is also generally used with static lists of data, but because we are lazy-loading the tree when
 * a user expands the directory, we need to manually "inject" the new children into the tree at the right place.
 */
const SnippetsTreeView: VoidFunctionComponent<Props> = ({ userRevisionLocator, selectedPath, setSelectedPath }) => {
  const addToast = useAddToast();
  //  Keep a reference to the tree. This is so that we can properly mark nodes as open/closed in the onToggle.
  const treeRef = useRef<TreeApi<SnippetTreeNodeData>>(null);
  //  Initially, just the root is displayed.
  const [data, setData] = useState<SnippetTreeNodeData[]>([ROOT_NODE]);
  //  This is so that we can do flat lookups when we need to inject nodes.
  //  This doesn't need to be state because it doesn't really power any UI/we don't need the re-render.
  const nodesByPath = useRef<Record<string, SnippetTreeNodeData>>({
    [ROOT_NODE.path]: ROOT_NODE,
  });

  //  This is an esoteric use of React Query, typically you should never need to use queryClient directly in this way.
  //  However, doing it like this allows us to retain the benefits of React Query (like the caching)
  //  while still being able to imperatively make a new query when a new directory is toggled.
  //  Don't copy this pattern unless you have tried doing things the "normal" way first.
  const queryClient = useQueryClient();

  //  This is a helper function to register the children in the nodesByPath ref.
  //  This is so that we can do flat lookups when we need to inject nodes.
  const registerChildren = (children: SnippetTreeNodeData[]) => {
    children.forEach((child) => {
      nodesByPath.current[child.path] = child;
    });
  };

  //  "inject" the newly loaded children into the tree and update the ref.
  const injectChildrenOnLoad = useCallback((parentPath: string, childNodes: SnippetTreeNodeData[]) => {
    //  Put each new child into the map
    registerChildren(childNodes);
    //  Now we need to update the parent entry with the updated object (so that it has the children).
    const parentNode = nodesByPath.current[parentPath];
    if (parentNode) {
      nodesByPath.current[parentPath] = {
        ...parentNode,
        children: childNodes,
        hasLoaded: true,
      };
    }
    //  Finally, we update the actual state data being passed to the Tree
    setData(prevData => injectChildren(prevData, parentPath, childNodes));
  }, []);

  //  Method for marking descendants of a node as "closed" within the react-arborist's internal state
  const closeDescendants = useCallback((node: SnippetTreeNodeData) => {
    const tree = treeRef?.current;
    if (tree) {
      node.children?.forEach(child => {
        tree.close(child.path);
        //  Recurse
        closeDescendants(child);
      });
    }
  }, [treeRef]);

  //  This is the function which triggers opening/closing a directory.
  //  When a directory is opened, we will manually trigger a fetch with that path.
  const handleToggle = useCallback(async (nodePath: string) => {
    //  Do a flat lookup to find the node.
    const node = nodesByPath.current[nodePath];
    //  Catch undefined, non-directory nodes
    if (!node || node.type !== 'directory') {
      return;
    }

    //  Situation 1: The folder has just been closed.
    //  In this case, we need to manually tell arborist that all of its descendants are also closed.
    const tree = treeRef?.current;
    //  Handle undefined (mostly for TypeScript)
    if (!tree) {
      return;
    }

    //  Check if the node is open AFTER toggling
    const isNowOpen = tree.isOpen(nodePath); // AFTER Arborist toggled
    if (!isNowOpen) {
      //  Tell arborist to close all the descendants
      closeDescendants(node);
      // tree.toggle(nodePath); // Tell arborist to flip it
      return;
    }

    //  Situation 2: The folder was just opened.
    //  In this case, we need to fetch the children of that node (if we haven't already).
    if (node.hasLoaded) {
      return;
    }

    //  This is to fix an issue where arborist needs path to be non-empty but the backend expects empty for root.
    const params: GetSnippetPathsParams = node.name === ROOT_NODE.name ? {} : {
      path: node.path,
    };

    //  `ensureQueryData` is an asynchronous function that can be used to get an existing query's cached data.
    //  If the query does not exist, queryClient.fetchQuery will be called and its results returned.
    const response = await queryClient.ensureQueryData({
      queryKey: getSnippetPathsQueryKey(userRevisionLocator, params),
      queryFn: () => getSnippetPaths(userRevisionLocator, params),
    });

    //  Handle error case by displaying a toast, then returning early before updating the tree.
    if (!response.data?.paths) {
      addToast({
        type: 'error',
        content: `Failed to load snippets for path: ${nodePath}`,
      });
      return;
    }

    //  "inject" the newly loaded children into the tree at the correct place, and update the ref.
    injectChildrenOnLoad(
      nodePath,
      response.data.paths.map(path => ({
        ...path,
        //  If `react-arborist` is passed a node with `children` set, it will treat the node as a directory which can be expanded.
        //  We do not want this for cases where it is a file, so we set `children` to `undefined` in that case.
        //  Later when this directory is expanded, we will "inject" the children into the tree.
        children: path.type === 'directory' ? [] : undefined,
      })),
    );
  }, [queryClient, userRevisionLocator, injectChildrenOnLoad, closeDescendants, addToast]);

  return (
    <div className={styles.treeView}>
      <div className={styles.treeViewHeader}>
        Files
      </div>
      <Tree<SnippetTreeNodeData>
        ref={treeRef}
        data={data}
        openByDefault={false}
        idAccessor="path" // Tells it we want to use the "path" as the ID
        childrenAccessor="children" // Tells it we want to use the "children" as the children
        onToggle={handleToggle}
        //  Layout
        rowHeight={36}
        indent={16}
        width={TREE_WIDTH}
        overscanCount={1}
        //  Selection logic
        selection={selectedPath}
        onSelect={(nodes: Array<NodeApi<SnippetTreeNodeData>>) => {
          //  react-arborist allows mutli-select, which is why it passes us an array of nodes.
          //  We only want to select the first node, so we take the first one.
          const node = nodes[0];
          if (node) {
            setSelectedPath(node.data.path);
            //  TODO: callback or something to tell the rest of the app which node is selected.
          }
        }}
        disableMultiSelection
        disableDrag
      >
        {SnippetsTreeViewNode}
      </Tree>
    </div>
  );
};

export default SnippetsTreeView;
